import { useState } from "react";
import { Link, useParams } from "react-router-dom";
import { ArrowUpRight, CheckCircle2, Circle, Clock, X, ZoomIn } from "lucide-react";
import { Seo, Reveal, scrollToId } from "../components/Seo";
import { PageHero, SectionHead, StatusPill, BtnGhost, CtaBand, ShotPlaceholder } from "../components/blocks";
import { DemoForm } from "../components/forms";
import { PRODUCTS } from "../lib/site";
import NotFound from "./NotFound";

function CardImage({ item, logoBg, onPreview }) {
  const isLogo = Boolean(item.isLogo);
  const isLandscape = item.image.includes("admin");

  return (
    <div
      role="button"
      tabIndex={0}
      className={`aspect-[16/10] w-full overflow-hidden border border-charcoal/10 cursor-pointer group relative flex items-center justify-center ${
        isLogo ? "" : "bg-charcoal/5"
      }`}
      style={isLogo ? { background: logoBg || "#0e3b2e" } : {}}
      onClick={() =>
        onPreview({
          src: item.image,
          title: item.label,
          caption: item.caption,
        })
      }
      onKeyDown={(e) => {
        if (e.key === "Enter" || e.key === " ") {
          onPreview({
            src: item.image,
            title: item.label,
            caption: item.caption,
          });
        }
      }}
    >
      <img
        src={item.image}
        alt={item.label}
        className={
          isLogo
            ? "max-h-56 max-w-[85%] object-contain drop-shadow-md group-hover:scale-105 transition-transform duration-300"
            : `w-full h-full object-cover ${isLandscape ? "object-center" : "object-top"} group-hover:scale-[1.02] transition-transform duration-300`
        }
      />
      <div className="absolute inset-0 bg-charcoal/30 opacity-0 group-hover:opacity-100 transition-opacity duration-200 flex items-center justify-center">
        <span className="bg-charcoal text-white font-mono text-xs uppercase tracking-wider px-3.5 py-2 rounded flex items-center gap-2 border border-white/20 shadow-lg">
          <ZoomIn size={14} className="text-lime" /> View Full Size
        </span>
      </div>
    </div>
  );
}

export default function ProductDetail() {
  const { slug } = useParams();
  const [previewImg, setPreviewImg] = useState(null);
  const p = PRODUCTS.find((x) => x.slug === slug);
  if (!p) return <NotFound />;

  const others = PRODUCTS.filter((x) => x.slug !== slug);

  return (
    <>
      <Seo
        title={`${p.name} — ${p.tagline} | Softline Nigeria`}
        description={p.short}
        path={`/products/${p.slug}`}
      />
      <PageHero
        eyebrow={`Softline product · ${p.tagline}`}
        title={[p.name]}
        lead={p.desc}
        crumbs={[{ label: "Home", to: "/" }, { label: "Products", to: "/products" }, { label: p.name }]}
      >
        <Reveal delay={0.45}>
          <div className="mt-9 flex flex-wrap items-center gap-4">
            <StatusPill status={p.status} />
            <button
              type="button"
              onClick={() => scrollToId("demo-form")}
              data-testid="product-cta-preview"
              className="inline-flex items-center justify-center gap-2 bg-lime text-charcoal font-semibold px-7 py-4 text-sm md:text-base hover:bg-limelight transition-colors duration-200"
            >
              {p.status.cta}
            </button>
          </div>
          <p className="mt-4 text-xs text-softgrey/70 max-w-lg">{p.status.note}</p>
        </Reveal>
      </PageHero>

      {/* Problem + overview */}
      <section className="bg-white" data-testid="product-problem">
        <div className="shell py-24 md:py-32 grid lg:grid-cols-2 gap-14">
          <div>
            <SectionHead dark={false} eyebrow="The business problem" title={p.problem.split(". ")[0] + "."} lead={p.problem.split(". ").slice(1).join(". ")} />
          </div>
          <div>
            <SectionHead dark={false} eyebrow="Product overview" title="One platform, one workflow." lead={p.overview} />
          </div>
        </div>
      </section>

      {/* How it works */}
      <section className="bg-surface" data-testid="product-how">
        <div className="shell py-24 md:py-32">
          <SectionHead dark={false} eyebrow="How it works" title="From first step to audit trail." />
          <ol className="mt-14 grid md:grid-cols-2 lg:grid-cols-5 gap-px bg-charcoal/10 border border-charcoal/10">
            {p.how.map((step, i) => (
              <Reveal key={i} delay={i * 0.06} className="h-full">
                <li className="h-full bg-surface p-7">
                  <span className="font-mono text-3xl font-medium text-lime block">{String(i + 1).padStart(2, "0")}</span>
                  <p className="mt-4 text-sm leading-relaxed text-charcoal/80">{step}</p>
                </li>
              </Reveal>
            ))}
          </ol>
        </div>
      </section>

      {/* Capabilities */}
      <section className="bg-white" data-testid="product-capabilities">
        <div className="shell py-24 md:py-32">
          <SectionHead
            dark={false}
            eyebrow="Core capabilities"
            title="What the platform does."
            lead="Distinguishing what is available today from what is on the roadmap is part of how we describe products honestly."
          />
          <div className="mt-10 flex flex-wrap gap-6 text-xs font-mono uppercase tracking-[0.15em]">
            <span className="inline-flex items-center gap-2"><CheckCircle2 size={15} className="text-lime" aria-hidden="true" /> Available</span>
            <span className="inline-flex items-center gap-2"><Clock size={15} className="text-charcoal/50" aria-hidden="true" /> Roadmap</span>
          </div>
          <div className="mt-8 grid md:grid-cols-2 lg:grid-cols-3 gap-px bg-charcoal/10 border border-charcoal/10">
            {p.capabilities.map((c, i) => (
              <Reveal key={c.title} delay={i * 0.04} className="h-full">
                <div className={`h-full p-7 ${c.status === "available" ? "bg-white" : "bg-surface"}`} data-testid={`product-cap-${i}`}>
                  <div className="flex items-start justify-between gap-3">
                    <h3 className="font-display font-bold text-[17px]">{c.title}</h3>
                    {c.status === "available" ? (
                      <CheckCircle2 size={18} className="text-lime shrink-0 mt-1" aria-label="Available" />
                    ) : (
                      <Clock size={18} className="text-charcoal/40 shrink-0 mt-1" aria-label="Roadmap" />
                    )}
                  </div>
                  <p className="mt-3 text-sm leading-relaxed text-charcoal/65">{c.desc}</p>
                  {c.status === "roadmap" ? (
                    <p className="mt-3 font-mono text-[10px] uppercase tracking-[0.2em] text-charcoal/45">Roadmap — not yet available</p>
                  ) : null}
                </div>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      {/* Product interface — verified screenshots */}
      <section className="bg-surface" data-testid="product-interface">
        <div className="shell py-24 md:py-32">
          <SectionHead
            dark={false}
            eyebrow="Product interface"
            title="Verified screenshots, not mockups."
            lead={
              p.screenshots
                ? "We publish only genuine product interfaces approved by the engineering and product team. Preview the verified recipient application view and business admin console below."
                : "We publish only genuine product interfaces, approved by the product team. Approved screenshots will replace the placeholders below before commercial launch."
            }
          />
          <div className="mt-14 grid md:grid-cols-2 gap-6">
            {/* 1. Recipient / Application View */}
            <Reveal>
              <div className="border border-charcoal/10 bg-white p-3 hover:border-charcoal/30 transition-all duration-200 flex flex-col h-full shadow-sm">
                <div className="flex items-center justify-between pb-3 mb-3 border-b border-charcoal/10">
                  <div className="flex items-center gap-2">
                    <span className="h-2 w-2 rounded-full bg-lime animate-pulse"></span>
                    <span className="font-mono text-[11px] uppercase tracking-[0.2em] text-charcoal/85 font-medium">
                      {p.screenshots?.recipient?.label || `${p.name} — recipient / application view`}
                    </span>
                  </div>
                  <span className="font-mono text-[10px] uppercase tracking-[0.15em] bg-charcoal text-white px-2.5 py-0.5 rounded font-semibold">
                    Verified
                  </span>
                </div>

                {p.screenshots?.recipient?.image ? (
                  <CardImage item={p.screenshots.recipient} logoBg={p.logoBg} onPreview={setPreviewImg} />
                ) : (
                  <ShotPlaceholder label={`${p.name} — recipient / application view`} tone="light" />
                )}

                <div className="mt-3 flex items-center justify-between text-xs text-charcoal/60 pt-1">
                  <p className="text-xs text-charcoal/70 truncate mr-2">
                    {p.screenshots?.recipient?.caption || "Approved product interface"}
                  </p>
                  {p.screenshots?.recipient?.image && (
                    <button
                      type="button"
                      onClick={() =>
                        setPreviewImg({
                          src: p.screenshots.recipient.image,
                          title: p.screenshots.recipient.label,
                          caption: p.screenshots.recipient.caption,
                        })
                      }
                      className="font-mono text-[10px] uppercase tracking-[0.15em] text-charcoal/70 hover:text-charcoal inline-flex items-center gap-1 font-semibold shrink-0"
                    >
                      Expand <ArrowUpRight size={12} className="text-lime" />
                    </button>
                  )}
                </div>
              </div>
            </Reveal>

            {/* 2. Business / Admin View */}
            <Reveal delay={0.1}>
              <div className="border border-charcoal/10 bg-white p-3 hover:border-charcoal/30 transition-all duration-200 flex flex-col h-full shadow-sm">
                <div className="flex items-center justify-between pb-3 mb-3 border-b border-charcoal/10">
                  <div className="flex items-center gap-2">
                    <span className="h-2 w-2 rounded-full bg-lime animate-pulse"></span>
                    <span className="font-mono text-[11px] uppercase tracking-[0.2em] text-charcoal/85 font-medium">
                      {p.screenshots?.admin?.label || `${p.name} — business / admin view`}
                    </span>
                  </div>
                  <span className="font-mono text-[10px] uppercase tracking-[0.15em] bg-charcoal text-white px-2.5 py-0.5 rounded font-semibold">
                    Verified
                  </span>
                </div>

                {p.screenshots?.admin?.image ? (
                  <CardImage item={p.screenshots.admin} logoBg={p.logoBg} onPreview={setPreviewImg} />
                ) : (
                  <ShotPlaceholder label={`${p.name} — business / admin view`} tone="light" />
                )}

                <div className="mt-3 flex items-center justify-between text-xs text-charcoal/60 pt-1">
                  <p className="text-xs text-charcoal/70 truncate mr-2">
                    {p.screenshots?.admin?.caption || "Approved product interface"}
                  </p>
                  {p.screenshots?.admin?.image && (
                    <button
                      type="button"
                      onClick={() =>
                        setPreviewImg({
                          src: p.screenshots.admin.image,
                          title: p.screenshots.admin.label,
                          caption: p.screenshots.admin.caption,
                        })
                      }
                      className="font-mono text-[10px] uppercase tracking-[0.15em] text-charcoal/70 hover:text-charcoal inline-flex items-center gap-1 font-semibold shrink-0"
                    >
                      Expand <ArrowUpRight size={12} className="text-lime" />
                    </button>
                  )}
                </div>
              </div>
            </Reveal>
          </div>
        </div>
      </section>

      {/* Integration + security (dark) */}
      <section className="bg-charcoal noise-bg" data-testid="product-integration">
        <div className="shell py-24 md:py-32 grid lg:grid-cols-2 gap-14">
          <div>
            <SectionHead dark eyebrow="Enterprise integration" title="Designed to fit your estate." lead={p.integration} />
          </div>
          <div>
            <SectionHead dark eyebrow="Security & privacy" title="Designed around sound principles." lead={p.security} />
          </div>
        </div>
      </section>

      {/* Target customers */}
      <section className="bg-white" data-testid="product-customers">
        <div className="shell py-20 md:py-28">
          <Reveal>
            <div className="border border-charcoal/10 bg-surface p-8 md:p-10">
              <p className="font-mono text-[11px] uppercase tracking-[0.25em] text-charcoal/60">Target customers</p>
              <p className="mt-4 font-display font-bold text-xl md:text-2xl max-w-3xl leading-snug">{p.customers}</p>
            </div>
          </Reveal>
        </div>
      </section>

      {/* Demo / preview request */}
      <section className="bg-surface" id="demo-form" data-testid="product-demo-section">
        <div className="shell py-24 md:py-32 grid lg:grid-cols-12 gap-14">
          <div className="lg:col-span-5">
            <SectionHead
              dark={false}
              eyebrow={p.status.cta}
              title={`See ${p.name} in a guided session.`}
              lead="Tell us your use case and we will arrange a session with the product team. Submitting this form does not confirm a meeting — we will contact you to agree a time."
            />
            <Reveal delay={0.15}>
              <div className="mt-8">
                <StatusPill status={p.status} dark={false} />
              </div>
            </Reveal>
          </div>
          <div className="lg:col-span-7 bg-white border border-charcoal/10 p-8 md:p-10">
            <Reveal delay={0.1}>
              <DemoForm productSlug={p.slug} />
            </Reveal>
          </div>
        </div>
      </section>

      {/* Other products */}
      <section className="bg-white" data-testid="product-others">
        <div className="shell pb-24">
          <div className="grid md:grid-cols-2 gap-6">
            {others.map((o, i) => (
              <Reveal key={o.slug} delay={i * 0.08}>
                <Link
                  to={`/products/${o.slug}`}
                  data-testid={`product-other-${o.slug}`}
                  className="group flex items-center justify-between border border-charcoal/10 p-7 hover:border-charcoal/30 transition-[border-color] duration-200"
                >
                  <div>
                    <p className="font-mono text-[10px] uppercase tracking-[0.2em] text-charcoal/50">Next product</p>
                    <h3 className="font-display font-bold text-2xl mt-2">{o.name}</h3>
                    <p className="text-sm text-charcoal/60 mt-1">{o.tagline}</p>
                  </div>
                  <ArrowUpRight size={24} className="text-charcoal/30 group-hover:text-lime transition-colors duration-200" />
                </Link>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      {/* Lightbox Preview Modal */}
      {previewImg && (
        <div
          role="dialog"
          aria-modal="true"
          className="fixed inset-0 z-50 bg-charcoal/90 backdrop-blur-sm flex items-center justify-center p-4 md:p-8"
          onClick={() => setPreviewImg(null)}
        >
          <div
            className="relative max-w-5xl w-full bg-charcoal border border-white/20 p-5 shadow-2xl flex flex-col rounded-sm"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="flex items-center justify-between pb-3 border-b border-white/10 text-white">
              <div>
                <p className="font-mono text-xs uppercase tracking-wider text-lime font-semibold">{previewImg.title}</p>
                {previewImg.caption && <p className="text-xs text-softgrey/80 mt-1">{previewImg.caption}</p>}
              </div>
              <button
                type="button"
                onClick={() => setPreviewImg(null)}
                className="text-softgrey hover:text-white p-1 text-xs font-mono uppercase border border-white/20 hover:border-white transition-colors flex items-center gap-1"
                aria-label="Close image modal"
              >
                <X size={14} /> Close
              </button>
            </div>
            <div className="mt-4 overflow-auto max-h-[75vh] flex items-center justify-center bg-black/40 p-2 rounded">
              <img
                src={previewImg.src}
                alt={previewImg.title}
                className="max-h-[70vh] w-auto max-w-full object-contain"
              />
            </div>
          </div>
        </div>
      )}
    </>
  );
}
